import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Input from '../ui/Input';
import FunProgress from '../ui/FunProgress';
import HistorySection from '../history/HistorySection';
import {
  Download,
  AlertCircle,
  CheckCircle,
  Image as ImageIcon,
  Instagram,
  Video,
  Archive,
  Grid,
  User,
  ChevronDown,
} from 'lucide-react';

interface MediaItem {
  url: string;
  type: 'image' | 'video';
  thumbnail?: string;
}

interface ProfilePost {
  url: string;
  thumbnail: string;
  type: string;
}

type Mode = 'single' | 'profile' | null;

const PROFILE_LOADING_MESSAGES = [
  '🕵️ Sneaking through Instagram...',
  '📸 Peeking at the grid...',
  '🔍 Hunting for posts...',
  '🌀 Scrolling through the feed...',
  '⏳ Instagram is being slow, hang tight...',
  '🤫 Quietly collecting thumbnails...',
];

function detectMode(url: string): Mode {
  if (!url) return null;
  if (/instagram\.com\/(p|reel|tv)\//.test(url)) return 'single';
  if (/instagram\.com\/[^/?#]+\/?$/.test(url) || /instagram\.com\/[^/?#]+\/?\?/.test(url)) return 'profile';
  return null;
}

const InstagramDownloader = () => {
  const [instagramUrl, setInstagramUrl]     = useState('');
  const [mode, setMode]                     = useState<Mode>(null);

  // Single post state
  const [loading, setLoading]               = useState(false);
  const [media, setMedia]                   = useState<MediaItem[]>([]);
  const [error, setError]                   = useState<string | null>(null);
  const [success, setSuccess]               = useState<string | null>(null);
  const [progress, setProgress]             = useState(0);

  // Profile mode state
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [profilePosts, setProfilePosts]     = useState<ProfilePost[]>([]);
  const [username, setUsername]             = useState('');
  const [profileProgress, setProfileProgress] = useState(0);
  const [profileMsg, setProfileMsg]         = useState('');
  const [profileError, setProfileError]     = useState<string | null>(null);
  const [displayLimit, setDisplayLimit]     = useState(12);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const msgIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Debounced mode detection
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const detected = detectMode(instagramUrl);
      setMode(detected);
    }, 500);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [instagramUrl]);

  const reset = () => {
    setError(null);
    setSuccess(null);
    setProgress(0);
    setMedia([]);
    setProfilePosts([]);
    setProfileError(null);
    setUsername('');
    setDisplayLimit(12);
  };

  const startMsgCycle = () => {
    let idx = 0;
    setProfileMsg(PROFILE_LOADING_MESSAGES[0]);
    msgIntervalRef.current = setInterval(() => {
      idx = (idx + 1) % PROFILE_LOADING_MESSAGES.length;
      setProfileMsg(PROFILE_LOADING_MESSAGES[idx]);
    }, 3000);
  };

  const stopMsgCycle = () => {
    if (msgIntervalRef.current) {
      clearInterval(msgIntervalRef.current);
      msgIntervalRef.current = null;
    }
  };

  // ── Single post fetch ──────────────────────────────────────────────────────
  const handleFetch = async () => {
    reset();
    if (!instagramUrl) return setError('Please enter an Instagram URL');
    setLoading(true);
    setProgress(10);
    try {
      setProgress(30);
      const resp = await fetch('/api/tools/instagram', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: instagramUrl }),
      });
      setProgress(80);
      const data = await resp.json();

      // Auto-handle profile response
      if (data.success && data.isProfile) {
        setProgress(100);
        setProfilePosts(data.posts || []);
        setUsername(data.username || '');
        setSuccess(data.message || `Found ${data.total} posts`);
        setLoading(false);
        return;
      }
      if (data.success && data.media) {
        setProgress(100);
        setMedia(data.media);
        setSuccess(data.message || `Found ${data.media.length} media item(s)`);
      } else {
        setError(data.error || 'Failed to fetch Instagram media');
      }
    } catch (err: any) {
      setError(err.message || 'Network error');
    } finally {
      setLoading(false);
    }
  };

  // ── Single item download ───────────────────────────────────────────────────
  const downloadSingle = async (mediaUrl: string, index: number) => {
    try {
      // Proxy URLs — append ?download=1 so the server sends Content-Disposition
      if (mediaUrl.startsWith('/api/tools/instagram/proxy-image')) {
        const sep = mediaUrl.includes('?') ? '&' : '?';
        const a = document.createElement('a');
        a.href = `${mediaUrl}${sep}download=1`;
        a.download = `instagram_media_${index + 1}`;
        a.click();
        return;
      }
      if (mediaUrl.startsWith('/api/download/') || mediaUrl.startsWith('http://localhost')) {
        const a = document.createElement('a');
        a.href = mediaUrl;
        a.download = mediaUrl.split('/').pop() || `download_${index}`;
        a.click();
        return;
      }
      const resp = await fetch('/api/tools/download', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: mediaUrl }),
      });
      const data = await resp.json();
      if (data.success) {
        const a = document.createElement('a');
        a.href = data.downloadUrl;
        a.download = data.filename;
        a.click();
      } else {
        setError(`Failed to download item ${index + 1}`);
      }
    } catch (err: any) {
      setError(`Error: ${err.message}`);
    }
  };

  const downloadAll = async () => {
    setSuccess('Downloading all…');
    for (let i = 0; i < media.length; i++) {
      await downloadSingle(media[i].url, i);
      await new Promise(r => setTimeout(r, 400));
    }
    setSuccess(`Downloaded ${media.length} item(s)!`);
  };

  // ── Download a profile post via existing single-post endpoint ──────────────
  const downloadProfilePost = async (postUrl: string) => {
    try {
      const resp = await fetch('/api/tools/instagram', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: postUrl }),
      });
      const data = await resp.json();
      if (data.success && data.media && data.media.length > 0) {
        for (let i = 0; i < data.media.length; i++) {
          await downloadSingle(data.media[i].url, i);
          await new Promise(r => setTimeout(r, 300));
        }
      } else {
        setError(data.error || 'Failed to download post');
      }
    } catch (err: any) {
      setError(err.message || 'Network error');
    }
  };

  // ── Download all profile posts as ZIP ─────────────────────────────────────
  const downloadAllProfilePosts = async () => {
    setSuccess(`Preparing ZIP of ${profilePosts.length} posts…`);
    try {
      const resp = await fetch('/api/tools/instagram/download-profile-zip', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ posts: profilePosts, username }),
      });
      if (!resp.ok) throw new Error('ZIP generation failed');
      const blob = await resp.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${username || 'instagram'}_posts.zip`;
      a.click();
      setSuccess(`Downloaded ${profilePosts.length} posts as ZIP!`);
    } catch (err: any) {
      setError(err.message || 'Failed to create ZIP');
    }
  };

  const proxyThumb = (url: string) =>
    url ? `/api/tools/instagram/proxy-image?url=${encodeURIComponent(url)}` : '';

  const visiblePosts = profilePosts.slice(0, displayLimit);
  const hasMore = displayLimit < profilePosts.length;

  return (
    <div className="space-y-6">
      {/* Hero */}
      <div className="tool-hero">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#54c4b6] to-[#216974] flex items-center justify-center shadow-lg shadow-[#54c4b6]/30">
            <Instagram size={16} className="text-white" />
          </div>
          <span className="text-xs font-semibold text-[#216974] bg-[#54c4b6]/15 px-2.5 py-1 rounded-full">Instagram</span>
        </div>
        <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight">Instagram Downloader</h1>
        <p className="text-sm text-gray-500 mt-1">
          Download photos, videos, carousels — or browse an entire profile grid.
        </p>
      </div>

      <div className="max-w-xl mx-auto space-y-5">
        <Card>
          <div className="space-y-5">
            <Input
              label="Instagram URL"
              placeholder="https://www.instagram.com/p/... or /username/"
              value={instagramUrl}
              onChange={(e) => { setInstagramUrl(e.target.value); reset(); }}
              leftIcon={<Instagram size={16} className="text-[#54c4b6]" />}
              hint={
                mode === 'profile'
                  ? '👤 Profile detected — will load post grid'
                  : mode === 'single'
                  ? '📸 Post/Reel detected — will download media'
                  : 'Paste any Instagram post, reel, or profile URL'
              }
            />

            {/* Mode badge */}
            <AnimatePresence>
              {mode && (
                <motion.div
                  key={mode}
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="flex items-center gap-2"
                >
                  {mode === 'profile' ? (
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-purple-700 bg-purple-50 border border-purple-200 px-2.5 py-1 rounded-full">
                      <User size={11} /> Profile Grid Mode
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#216974] bg-[#54c4b6]/10 border border-[#54c4b6]/30 px-2.5 py-1 rounded-full">
                      <ImageIcon size={11} /> Single Post Mode
                    </span>
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Progress bars */}
            <AnimatePresence>
              {loading && progress > 0 && (
                <motion.div key="prog-single" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  className="glass-card rounded-2xl p-4">
                  <FunProgress percent={progress} tool="instagram" />
                </motion.div>
              )}
              {loadingProfile && (
                <motion.div key="prog-profile" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  className="glass-card rounded-2xl p-4 space-y-3">
                  <FunProgress percent={profileProgress} tool="instagram" />
                  <p className="text-xs text-center text-gray-500 animate-pulse">{profileMsg}</p>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Alerts */}
            <AnimatePresence>
              {(error || profileError) && (
                <motion.div key="err" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="alert-error">
                  <AlertCircle size={16} className="shrink-0" />
                  <p className="text-sm font-medium">{error || profileError}</p>
                </motion.div>
              )}
              {success && (
                <motion.div key="ok" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="alert-success">
                  <CheckCircle size={16} className="shrink-0" />
                  <p className="text-sm font-medium">{success}</p>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Action button — single button handles both post and profile */}
            <Button
              onClick={handleFetch}
              size="lg"
              className="w-full"
              isLoading={loading || loadingProfile}
              disabled={!instagramUrl || loading || loadingProfile}
              leftIcon={mode === 'profile' ? <Grid size={18} /> : <ImageIcon size={18} />}
            >
              {loading || loadingProfile
                ? mode === 'profile' ? 'Loading Profile…' : 'Fetching Media…'
                : mode === 'profile' ? 'Load Profile Grid' : 'Fetch Instagram Media'}
            </Button>
          </div>
        </Card>

        {/* ── Single post media grid ── */}
        <AnimatePresence>
          {media.length > 0 && (
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
              <Card
                title={`Found ${media.length} ${media.length === 1 ? 'Item' : 'Items'}`}
                titleAction={
                  media.length > 1
                    ? <button onClick={downloadAll}
                        className="flex items-center gap-1.5 text-xs font-semibold text-[#216974] hover:text-[#216974] transition-colors">
                        <Archive size={13} /> Download All
                      </button>
                    : undefined
                }
              >
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {media.map((item, i) => (
                    <motion.div key={i}
                      initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: i * 0.06 }}
                      className="group relative"
                    >
                      <div className="aspect-square rounded-2xl overflow-hidden bg-gray-100 border border-gray-200/60">
                        {item.url.endsWith('.zip') ? (
                          <div className="w-full h-full flex flex-col items-center justify-center gap-2">
                            <Archive size={32} className="text-[#216974]" />
                            <span className="text-xs text-gray-500 font-medium">Archive</span>
                          </div>
                        ) : (
                          <>
                            {item.type === 'video'
                              ? <div className="w-full h-full flex items-center justify-center bg-gray-900">
                                  <Video size={32} className="text-white/60" />
                                </div>
                              : <img src={item.url} alt={`Media ${i + 1}`}
                                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                  crossOrigin="anonymous" />
                            }
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-200 rounded-2xl flex items-center justify-center">
                              <motion.div
                                initial={{ opacity: 0, scale: 0.8 }} whileHover={{ opacity: 1, scale: 1 }}
                                className="opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <button onClick={() => downloadSingle(item.url, i)}
                                  className="w-10 h-10 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-lg">
                                  <Download size={16} className="text-gray-800" />
                                </button>
                              </motion.div>
                            </div>
                          </>
                        )}
                      </div>
                      <Button onClick={() => downloadSingle(item.url, i)} size="xs" variant="secondary" className="w-full mt-2"
                        leftIcon={<Download size={12} />}>
                        {item.type === 'video' ? 'Video' : 'Image'} {i + 1}
                      </Button>
                    </motion.div>
                  ))}
                </div>

                {media.length > 1 && (
                  <Button onClick={downloadAll} size="lg" className="w-full mt-5"
                    leftIcon={<Download size={18} />}>
                    Download All ({media.length} items)
                  </Button>
                )}
              </Card>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Profile post grid ── */}
        <AnimatePresence>
          {profilePosts.length > 0 && (
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
              <Card
                title={`Found ${profilePosts.length} posts from @${username}`}
                titleAction={
                  <button
                    onClick={downloadAllProfilePosts}
                    className="flex items-center gap-1.5 text-xs font-semibold text-[#216974] hover:text-[#216974] transition-colors"
                  >
                    <Archive size={13} /> Download All as ZIP
                  </button>
                }
              >
                <div className="grid grid-cols-3 gap-2">
                  {visiblePosts.map((post, i) => (
                    <motion.div
                      key={post.url}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: i * 0.03 }}
                      className="group relative"
                    >
                      <div className="aspect-square rounded-xl overflow-hidden bg-gray-100 border border-gray-200/60 cursor-pointer"
                        onClick={() => window.open(post.url, '_blank')}>
                        {post.thumbnail ? (
                          <img
                            src={proxyThumb(post.thumbnail)}
                            alt={`Post ${i + 1}`}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            loading="lazy"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            {post.type === 'reel'
                              ? <Video size={24} className="text-gray-400" />
                              : <ImageIcon size={24} className="text-gray-400" />
                            }
                          </div>
                        )}

                        {/* Type badge */}
                        {post.type === 'reel' && (
                          <div className="absolute top-1.5 left-1.5">
                            <span className="text-[10px] font-bold text-white bg-black/60 backdrop-blur-sm px-1.5 py-0.5 rounded-full">
                              REEL
                            </span>
                          </div>
                        )}

                        {/* Hover overlay with download button */}
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-200 rounded-xl flex items-center justify-center">
                          <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={(e) => { e.stopPropagation(); downloadProfilePost(post.url); }}
                              className="w-9 h-9 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-lg hover:bg-white transition-colors"
                            >
                              <Download size={14} className="text-gray-800" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Load more */}
                {hasMore && (
                  <button
                    onClick={() => setDisplayLimit(prev => prev + 12)}
                    className="w-full mt-4 py-2.5 text-sm font-semibold text-[#216974] border border-[#54c4b6]/40 rounded-xl hover:bg-[#54c4b6]/10 transition-colors flex items-center justify-center gap-2"
                  >
                    <ChevronDown size={16} />
                    Load More ({profilePosts.length - displayLimit} remaining)
                  </button>
                )}

                <Button
                  onClick={downloadAllProfilePosts}
                  size="lg"
                  className="w-full mt-4"
                  leftIcon={<Download size={18} />}
                >
                  Download All ({profilePosts.length} posts)
                </Button>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>

        {/* History */}
        <HistorySection toolId="instagram" />
      </div>
    </div>
  );
};

export default InstagramDownloader;
